// razorpayCheckout.js
//
// Thin wrapper around Razorpay's Standard Checkout (checkout.js). No React,
// no axios — just script loading + opening the modal. Order creation and
// payment verification stay in RestaurantOrder.js (via razorpay-axios.js)
// since those are network calls against our own backend, not Razorpay's.

const CHECKOUT_SCRIPT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

let scriptLoadPromise = null;

export function loadRazorpayScript() {
    if (window.Razorpay) return Promise.resolve();
    if (scriptLoadPromise) return scriptLoadPromise;

    scriptLoadPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = CHECKOUT_SCRIPT_SRC;
        script.onload = () => resolve();
        script.onerror = () => {
            scriptLoadPromise = null; // allow retry on next call
            reject(new Error('Failed to load Razorpay checkout script'));
        };
        document.body.appendChild(script);
    });

    return scriptLoadPromise;
}

// order:   { id, amount, currency } — the Razorpay order returned by our
//          backend's /create-order endpoint.
// keyId:   Razorpay's publishable key_id, also returned by /create-order.
// onSuccess({ razorpay_order_id, razorpay_payment_id, razorpay_signature })
// onFailure(error) — called on script-load failure, or if the checkout
//          modal is dismissed/cancelled without paying.
export async function openRazorpayCheckout({ order, keyId, name, description, prefill, onSuccess, onFailure }) {
    try {
        await loadRazorpayScript();
    } catch (err) {
        onFailure && onFailure(err);
        return;
    }

    const rzp = new window.Razorpay({
        key: keyId,
        amount: order.amount,
        currency: order.currency,
        order_id: order.id,
        name: name || 'POS Order',
        description: description || '',
        prefill: prefill || {},
        // No method/config restriction — shows Razorpay's normal
        // method-picker (Card/UPI/Netbanking/Wallet/...), whichever methods
        // are actually enabled on the account. The cashier picks UPI there
        // to get the scannable QR. Reverted from a UPI-only config.display
        // restriction that was hitting "No appropriate payment method
        // found" — likely UPI isn't enabled for this account/key yet; once
        // it is (Dashboard → Settings → Payment Methods), the UPI-only
        // config.display.blocks approach from before can be reapplied.
        handler: (response) => {
            onSuccess && onSuccess(response);
        },
        modal: {
            ondismiss: () => {
                onFailure && onFailure(new Error('Payment cancelled'));
            },
        },
    });

    rzp.on('payment.failed', (response) => {
        onFailure && onFailure(response.error || new Error('Payment failed'));
    });

    rzp.open();
}
